/**
 * POPSTAR LIVE — the PROGRAMME GUIDE (owner-picked v1, 2026-07-17). No longer a
 * placeholder: a glamorous broadcast lineup of REAL, server-validated runs that
 * tap-to-watch through the existing deterministic replay player —
 *   • ON AIR / NETWORK BEST — the reigning top run, crowned;
 *   • FRESH RUNS            — the newest broadcasts (names, scores, durations;
 *                             sub-5s noise is filtered by the feed read);
 *   • FEATURED              — admin-curated picks (`admin.featureReplay`).
 *
 * "PLEASE STAND BY" — tapping Watch shows a broadcast-style interstitial
 * (scanlines + blinking dot) while the full RunRecord loads, with a minimum
 * on-screen hold so it reads as a moment, not a flicker; then the shell's
 * full-screen replay player takes the stage.
 *
 * TRUST: everything listed here passed the server re-simulation at submit time
 * (only validated runs are stored), records are size-capped at submission, and
 * durations are shown so viewers pick their commitment.
 *
 * ARCHITECTURE: presentational + thin orchestration — one-shot loads through
 * the {@link GameServices} facade, no per-frame state; the parent owns the
 * replay player via {@link LiveScreenProps.onWatchReplay}.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AttackFamily, RunRecord } from '../core/types';
import type { GameServices, ScoreEntry } from '../services/types';
import type { RunSummary } from '../services/dataModel';
import { useT } from './i18n';

interface LiveScreenProps {
  services: GameServices;
  /** Launch the full-screen deterministic replay player with a loaded record. */
  onWatchReplay: (record: RunRecord) => void;
  /** Back to the previous screen (parent owns navigation). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own header. */
  embedded?: boolean;
}

/** Human label per attack family, for the "died to" tag. */
const FAMILY_LABEL: Record<AttackFamily, string> = {
  walls: 'Walls',
  lasers: 'Lasers',
  turret: 'Turret',
  caro: 'Caro',
  missiles: 'Missiles',
  bombs: 'Bombs',
};

/** Minimum time the PLEASE STAND BY card stays up (broadcast moment, not flicker). */
const STANDBY_HOLD_MS = 1100;

/** mm:ss from milliseconds (broadcast-listing style). */
function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Compact "3m ago" stamp for the fresh-runs feed. */
function timeAgo(epochMs: number, now: number): string {
  const diff = Math.max(0, now - epochMs);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function LiveScreen({ services, onWatchReplay, onBack, embedded = false }: LiveScreenProps) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [best, setBest] = useState<ScoreEntry | null>(null);
  const [fresh, setFresh] = useState<RunSummary[]>([]);
  const [featured, setFeatured] = useState<RunSummary[]>([]);
  /** True while a broadcast is being tuned in (the PLEASE STAND BY card). */
  const [standby, setStandby] = useState(false);
  const [loadedAt] = useState(() => Date.now());

  // One-shot lineup load; each read fails soft so one hiccup can't kill the show.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [board, recent, picks] = await Promise.all([
        services.getLeaderboard(1).catch(() => [] as ScoreEntry[]),
        services.getRecentRuns(8).catch(() => [] as RunSummary[]),
        services.getFeaturedRuns(4).catch(() => [] as RunSummary[]),
      ]);
      if (cancelled) return;
      setBest(board[0] ?? null);
      setFresh(recent);
      setFeatured(picks);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [services]);

  /**
   * Tune in: load the full record behind the PLEASE STAND BY card (with the
   * minimum hold), then hand it to the shell's replay player.
   */
  const watch = useCallback(
    (runId: string) => {
      void (async () => {
        setStandby(true);
        setError(null);
        const hold = new Promise((resolve) => window.setTimeout(resolve, STANDBY_HOLD_MS));
        try {
          const [record] = await Promise.all([services.getReplay(runId), hold]);
          if (!record) {
            setError(t('live.gone'));
            return;
          }
          onWatchReplay(record);
        } catch (err) {
          setError(err instanceof Error ? err.message : t('live.gone'));
        } finally {
          setStandby(false);
        }
      })();
    },
    [services, onWatchReplay, t],
  );

  const hasAnything = best !== null || fresh.length > 0 || featured.length > 0;

  return (
    <div className={'screen network-screen live-screen' + (embedded ? ' is-embedded' : '')}>
      <style>{LIVE_CSS}</style>

      {!embedded && (
        <header className="network-head">
          <button className="btn btn-ghost network-back" onClick={onBack} type="button">
            ‹ {t('common.back')}
          </button>
          <h1 className="network-title">POPSTAR LIVE</h1>
        </header>
      )}

      <p className="live-sub">{t('live.sub')}</p>

      {loading ? (
        <p className="live-status muted-line">{t('live.tuning')}</p>
      ) : !hasAnything ? (
        <section className="panel live-empty">
          <p className="live-empty-icon" aria-hidden>
            📺
          </p>
          <p className="live-status muted-line">{t('live.empty')}</p>
        </section>
      ) : (
        <>
          {/* ---- ON AIR: the reigning network-best run ---- */}
          {best && (
            <section className="panel live-onair">
              <span className="live-onair-badge">
                <span className="live-onair-dot" aria-hidden />
                {t('live.onAir')}
              </span>
              <span className="live-onair-crown" aria-hidden>
                👑
              </span>
              <span className="live-onair-name">{best.name}</span>
              <span className="live-onair-score">{best.score.toLocaleString()}</span>
              <span className="live-onair-cap">{t('live.headline')}</span>
              {best.runId && (
                <button
                  className="btn btn-primary live-onair-watch"
                  type="button"
                  disabled={standby}
                  onClick={() => watch(best.runId!)}
                >
                  ▶ {t('common.watch', 'Watch')}
                </button>
              )}
            </section>
          )}

          {/* ---- FEATURED: admin-curated picks ---- */}
          {featured.length > 0 && (
            <section className="panel live-block">
              <h2 className="live-block-head">⭐ {t('live.featured')}</h2>
              <ul className="live-list">
                {featured.map((run) => (
                  <LiveRow key={run.id} run={run} now={loadedAt} standby={standby} onWatch={watch} />
                ))}
              </ul>
            </section>
          )}

          {/* ---- FRESH RUNS: the newest broadcasts ---- */}
          {fresh.length > 0 && (
            <section className="panel live-block">
              <h2 className="live-block-head">🎬 {t('live.fresh')}</h2>
              <ul className="live-list">
                {fresh.map((run) => (
                  <LiveRow key={run.id} run={run} now={loadedAt} standby={standby} onWatch={watch} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {error && (
        <p className="live-error" role="alert">
          {error}
        </p>
      )}

      {/* ---- PLEASE STAND BY: the broadcast interstitial while a record loads ---- */}
      {standby && (
        <div className="live-standby" role="status" aria-live="assertive">
          <div className="live-standby-card">
            <span className="live-standby-tv" aria-hidden>
              📺
            </span>
            <span className="live-standby-title">
              {t('live.standby')}
              <span className="live-standby-blink" aria-hidden>
                ●
              </span>
            </span>
            <span className="live-standby-sub">{t('live.tuning')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** One broadcast row: who, how far they got, how it ended, and Watch. */
function LiveRow({
  run,
  now,
  standby,
  onWatch,
}: {
  run: RunSummary;
  now: number;
  standby: boolean;
  onWatch: (runId: string) => void;
}) {
  const t = useT();
  return (
    <li className="live-row">
      <span className="live-row-meta">
        <span className="live-row-name">{run.name ?? 'Player'}</span>
        <span className="live-row-tags">
          <b className="live-row-score">{run.score.toLocaleString()}</b>
          <span className="live-row-tag">{formatDuration(run.durationMs)}</span>
          {run.deathFamily && (
            <span className="live-row-tag live-row-death">
              {t('family.' + run.deathFamily, FAMILY_LABEL[run.deathFamily])}
            </span>
          )}
          <span className="live-row-ago">{timeAgo(run.createdAt, now)}</span>
        </span>
      </span>
      <button
        className="btn btn-ghost live-row-watch"
        type="button"
        disabled={standby}
        onClick={() => onWatch(run.id)}
      >
        ▶ {t('common.watch', 'Watch')}
      </button>
    </li>
  );
}

/**
 * Screen-scoped styles (nested under `.live-screen` so nothing leaks). Shared
 * primitives (`.panel`, `.btn`, the network palette re-map, `pulse-dot`) come
 * from src/index.css; the programme-guide layout + the standby card live here.
 */
const LIVE_CSS = `
.live-screen { gap: 14px; }
.live-screen .live-sub {
  margin: 0;
  width: min(440px, 92vw);
  color: var(--muted);
  font-size: 13px;
}
.live-screen .live-status { margin: 0; font-size: 13px; }
.live-screen .live-error {
  margin: 0;
  width: min(440px, 92vw);
  text-align: center;
  color: var(--danger);
  font-size: 13px;
}

/* ---- ON AIR headline ---- */
.live-screen .live-onair {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-areas:
    'badge badge badge'
    'crown name  watch'
    'crown score watch'
    'crown cap   watch';
  align-items: center;
  column-gap: 14px;
}
.live-screen .live-onair-badge {
  grid-area: badge;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  justify-self: start;
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.18em;
  color: var(--accent-2);
}
.live-screen .live-onair-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--accent-2);
  animation: pulse-dot 1.6s var(--ease) infinite;
}
.live-screen .live-onair-crown { grid-area: crown; font-size: 34px; }
.live-screen .live-onair-name {
  grid-area: name;
  font-size: 16px;
  font-weight: 800;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-screen .live-onair-score {
  grid-area: score;
  font-size: 34px;
  font-weight: 900;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(96deg, var(--accent-soft), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 2px 14px var(--accent-glow));
}
.live-screen .live-onair-cap {
  grid-area: cap;
  font-size: 10.5px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.live-screen .live-onair-watch { grid-area: watch; white-space: nowrap; }

/* ---- Lineup blocks ---- */
.live-screen .live-block-head {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 800;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.live-screen .live-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.live-screen .live-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
}
.live-screen .live-row-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}
.live-screen .live-row-name {
  font-size: 13px;
  font-weight: 800;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-screen .live-row-tags {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 11.5px;
  color: var(--text-dim);
  font-variant-numeric: tabular-nums;
}
.live-screen .live-row-score { font-size: 14px; font-weight: 900; color: var(--gold); }
.live-screen .live-row-death { color: var(--accent-2); }
.live-screen .live-row-death::before { content: '☠ '; opacity: 0.8; }
.live-screen .live-row-ago { color: var(--muted); }
.live-screen .live-row-watch { flex: 0 0 auto; padding: 7px 12px; font-size: 13px; }

/* ---- Empty stage ---- */
.live-screen .live-empty { width: min(440px, 92vw); text-align: center; }
.live-screen .live-empty-icon { font-size: 40px; margin: 4px 0 8px; }

/* ---- PLEASE STAND BY (broadcast interstitial) ---- */
.live-screen .live-standby {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: grid;
  place-items: center;
  background:
    repeating-linear-gradient(
      0deg,
      rgba(255, 255, 255, 0.025) 0px,
      rgba(255, 255, 255, 0.025) 1px,
      transparent 1px,
      transparent 4px
    ),
    rgba(8, 5, 16, 0.92);
  animation: fade-in var(--t) var(--ease) both;
}
.live-screen .live-standby-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 34px 46px;
  background: var(--panel-strong);
  border: 1px solid var(--glass-border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-3), 0 0 60px var(--accent-glow);
  text-align: center;
}
.live-screen .live-standby-tv { font-size: 44px; }
.live-screen .live-standby-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 19px;
  font-weight: 900;
  letter-spacing: 0.24em;
  color: var(--text);
}
.live-screen .live-standby-blink {
  color: var(--accent-2);
  font-size: 13px;
  animation: live-blink 1s steps(2, start) infinite;
}
@keyframes live-blink {
  to { visibility: hidden; }
}
.live-screen .live-standby-sub {
  font-size: 12px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.14em;
}
@media (prefers-reduced-motion: reduce) {
  .live-screen .live-standby-blink, .live-screen .live-onair-dot { animation: none; }
}
`;
